/**
 * LogService - Centralized logging service for debugging application state
 * Following Single Responsibility Principle (SRP)
 */

class LogService {
  constructor() {
    this.logCount = 0;
    this.fretboardLogCount = 0;
  }

  /**
   * Main method to log complete application state
   * @param {Object} state - Complete application state object
   */
  logAppState(state) {
    // Clear console for clean output
    console.clear();
    
    this.logCount++;
    const timestamp = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
      fractionalSecondDigits: 3 
    });

    console.group(`%c🎵 Chord Drone App State [${timestamp}] #${this.logCount}`, 'color: #2196f3; font-weight: bold; font-size: 14px');
    
    try {
      this.logMusicalState(state);
      this.logGuitarNeckState(state);
      this.logUIState(state);
      this.logAudioState(state);
      this.logProgressionState(state);
      this.logRecordingState(state);
      this.logComputedValues(state);
      
      // Add note about fretboard logging
      
    } catch (error) {
      console.error('Error logging app state:', error);
    } finally {
      console.groupEnd();
    }
  }

  /**
   * Log musical state information
   */
  logMusicalState(state) {
    console.group('Musical State');
    console.groupEnd();
  }

  /**
   * Log guitar neck visualization state
   */
  logGuitarNeckState(state) {
    console.group('%c🎸 Guitar Neck State', 'color: #ff5722; font-weight: bold');
    if (state.selectedNotes?.length) {
      console.table(state.selectedNotes.map((note, i) => ({
        index: i,
        string: note.string,
        fret: note.fret,
        note: note.note
      })));
    }
    console.groupEnd();
  }

  /**
   * Log UI and navigation state
   */
  logUIState(state) {
    console.group('%c🖥️ UI State', 'color: #00bcd4; font-weight: bold');
    console.groupEnd();
  }

  /**
   * Log audio engine state
   */
  logAudioState(state) {
    console.group('%c🔊 Audio State', 'color: #795548; font-weight: bold');
    
    console.group('🥁 Metronome');
    console.groupEnd();
    
    console.groupEnd();
  }

  /**
   * Log chord progression state
   */
  logProgressionState(state) {
    console.group('%c🎹 Progression State', 'color: #607d8b; font-weight: bold');
    
    // Calculate and display timing information
    if (state.barsPerChord) {
      const bpm = state.metronomeBPM || 120; // Default to 120 BPM if not set
      const timeSignature = state.metronomeTimeSignature || '4/4';
      const beatsPerBar = parseInt(timeSignature.split('/')[0]);
      const beatsPerChord = state.barsPerChord * beatsPerBar;
      const secondsPerBeat = 60 / bpm;
      const secondsPerChord = beatsPerChord * secondsPerBeat;
      
    }
    
    // Log progression notes if available
    if (state.currentProgression && state.progressionData) {
      const { progression, chords, degrees } = state.progressionData;
      
      if (progression) {
        console.group('📝 Progression Details');
        if (progression.numerals) {
        }
        
        if (chords && chords.length > 0) {
          console.table(chords.map((note, i) => ({
            position: i + 1,
            degree: progression.degrees?.[i] || 'N/A',
            numeral: progression.numerals?.[i] || 'N/A',
            note: note,
            quality: degrees?.[i]?.quality || 'N/A'
          })));
        }
        console.groupEnd();
      }
    } else if (state.currentProgression && state.notesInMode) {
      // Fallback: Calculate progression notes if not provided
      console.group('📝 Progression Notes (calculated)');
      try {
        // Import necessary functions inline to avoid circular dependencies
        const progressions = {
          'I-IV-V': { degrees: [1, 4, 5] },
          'I-V-vi-IV': { degrees: [1, 5, 6, 4] },
          'I-vi-ii-V': { degrees: [1, 6, 2, 5] },
          'ii-V-I': { degrees: [2, 5, 1] },
          'I-II-IV': { degrees: [1, 2, 4] },
          'i-VII-IV-I': { degrees: [1, 7, 4, 1] },
          'i-iv-v': { degrees: [1, 4, 5] },
          'i-VII-VI-V': { degrees: [1, 7, 6, 5] }
        };
        
        const progression = progressions[state.currentProgression];
        if (progression && state.notesInMode) {
          const chordNotes = progression.degrees.map(degree => {
            const index = (degree - 1) % state.notesInMode.length;
            return state.notesInMode[index];
          });
          
        }
      } catch (error) {
      }
      console.groupEnd();
    }
    
    console.groupEnd();
  }

  /**
   * Log recording state
   */
  logRecordingState(state) {
    console.group('%c⏺️ Recording State', 'color: #f44336; font-weight: bold');
    console.groupEnd();
  }

  /**
   * Log computed values from the current state
   */
  logComputedValues(state) {
    if (!state.notesInMode && !state.chordNames) return;
    
    console.group('%c🔢 Computed Values', 'color: #9e9e9e; font-weight: bold');
    
    if (state.notesInMode) {
    }
    
    if (state.chordNames) {
      console.table(state.chordNames.map((chord, i) => ({
        index: i,
        note: state.notesInMode?.[i],
        chord: chord
      })));
    }
    
    if (state.activeScale) {
    }
    
    console.groupEnd();
  }

  /**
   * Log a custom message with styling
   */
  logCustom(message, data, style = 'color: #2196f3; font-weight: bold') {
  }

  /**
   * Log an error with context
   */
  logError(context, error, additionalData = {}) {
    console.group(`%c❌ Error in ${context}`, 'color: #f44336; font-weight: bold; font-size: 12px');
    console.error('Error:', error);
    console.groupEnd();
  }

  /**
   * Log a warning with context
   */
  logWarning(context, message, data = {}) {
    console.group(`%c⚠️ Warning in ${context}`, 'color: #ff9800; font-weight: bold');
    console.warn(message);
    if (Object.keys(data).length > 0) {
    }
    console.groupEnd();
  }

  /**
   * Log performance metrics
   */
  logPerformance(label, startTime) {
    const duration = performance.now() - startTime;
    const color = duration < 16 ? '#4caf50' : duration < 50 ? '#ff9800' : '#f44336';
  }

  /**
   * Log comprehensive fretboard visual state for debugging
   * @param {Object} fretboardData - Collected fretboard state data
   */
  logFretboardState(fretboardData) {
    // Clear console for clean output
    console.clear();
    
    this.fretboardLogCount++;
    const timestamp = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
      fractionalSecondDigits: 3 
    });

    console.group(`%c🎸 Fretboard Visual State [${timestamp}] #${this.fretboardLogCount}`, 'color: #ff5722; font-weight: bold; font-size: 16px');
    
    try {
      // Summary statistics
      console.group('%c📊 Summary Statistics', 'color: #2196f3; font-weight: bold');
      console.groupEnd();

      // Context information
      console.group('%c🎵 Musical Context', 'color: #9c27b0; font-weight: bold');
      if (fretboardData.contextData.ActiveScale) {
      } else {
      }
      
      // Display selected chord information
      if (fretboardData.contextData.SelectedChord) {
        const scaleRoot = fretboardData.contextData.ActiveScale?.scaleRoot;
        const scaleNotes = fretboardData.contextData.ActiveScale?.notes;
        const selectedChord = fretboardData.contextData.SelectedChord;
        
        if (scaleRoot && scaleNotes) {
          const chordIndex = scaleNotes.indexOf(selectedChord);
          const romanNumeral = chordIndex !== -1 ? ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'][chordIndex] || `${chordIndex + 1}` : '?';
        } else {
        }
      } else {
      }
      
      console.groupEnd();

      // Main fretboard data table - compact view
      console.group('%c🔍 Fretboard Overview Table', 'color: #4caf50; font-weight: bold');
      const compactData = fretboardData.fretboardData
        .filter(note => note.IsInScale) // Only show notes that are in scale
        .map(note => ({
          Position: note.Position,
          Note: note.DisplayNote,
          InScale: note.IsInScale ? '✓' : '✗',
          Root: note.IsRootNote ? '🎯' : '',
          Selected: note.IsSelected ? '🟠' : '',
          Arpeggio: note.IsArpeggio ? '🎼' : '',
          Pattern: note.IsInPattern ? '📝' : '',
          Chord: note.IsChordHighlight ? '🎹' : ''
        }));
      console.table(compactData);
      console.groupEnd();

      // Detailed breakdown by string
      console.group('%c🎸 String-by-String Breakdown', 'color: #ff9800; font-weight: bold');
      const GUITAR_TUNING = ['E', 'B', 'G', 'D', 'A', 'E'];
      GUITAR_TUNING.forEach((stringName, stringIndex) => {
        const stringNotes = fretboardData.fretboardData
          .filter(n => n.StringIndex === stringIndex && n.IsInScale)
          .map(note => ({
            Position: note.Position,
            Note: note.DisplayNote,
            InScale: note.IsInScale ? '✓' : '✗',
            Root: note.IsRootNote ? '🎯' : '',
            Selected: note.IsSelected ? '🟠' : '',
            Arpeggio: note.IsArpeggio ? '🎼' : '',
            Pattern: note.IsInPattern ? '📝' : '',
            Chord: note.IsChordHighlight ? '🎹' : ''
          }));
        
        if (stringNotes.length > 0) {
          console.groupCollapsed(`String ${stringIndex + 1} (${stringName}) - ${stringNotes.length} notes`);
          console.table(stringNotes);
          console.groupEnd();
        }
      });
      console.groupEnd();

      // Flat LLM Fretboard State
      console.group('%c📋 Flat LLM Fretboard State', 'color: #673ab7; font-weight: bold');
      const flatStateEntries = fretboardData.fretboardData
        .filter(note => note.IsInScale) // Only show notes that are in scale
        .map(note => {
          const attributes = [];
          if (note.IsInPattern) attributes.push('Pattern');
          if (note.IsRootNote) attributes.push('Root');
          if (note.IsChordHighlight) attributes.push('Chord');
          if (note.IsSelected) attributes.push('Selected');
          if (note.IsArpeggio) attributes.push('Arpeggio');
          if (note.IsModeColor) attributes.push('ModeColor');
          
          if (attributes.length === 0) attributes.push('NotPattern');
          
          return `${note.Position}-${attributes.join('-')}`;
        });
      
      console.groupEnd();

      // Visual State Debugging
      console.group('%c🔧 Visual State Debug Info', 'color: #607d8b; font-weight: bold');
      const selectedNotes = fretboardData.fretboardData.filter(n => n.IsSelected);
      const arpeggioNotes = fretboardData.fretboardData.filter(n => n.IsArpeggio);
      const chordHighlights = fretboardData.fretboardData.filter(n => n.IsChordHighlight);
      const rootNotes = fretboardData.fretboardData.filter(n => n.IsRootNote);
      const modeColorNotes = fretboardData.fretboardData.filter(n => n.IsModeColor);

      if (selectedNotes.length > 0) {
      }
      if (arpeggioNotes.length > 0) {
      }
      if (chordHighlights.length > 0) {
      }
      if (rootNotes.length > 0) {
      }
      if (modeColorNotes.length > 0) {
      }
      console.groupEnd();

      // Raw data for LLM analysis
      console.groupCollapsed('%c📄 Raw Data (for LLM analysis)');
      console.groupEnd();

    } catch (error) {
      console.error('Error logging fretboard state:', error);
    } finally {
      console.groupEnd();
    }
  }

}

// Export singleton instance
const logService = new LogService();
export default logService;